/**
 * TaskEditModal — extracted from GroupTasksPanel so both the inline
 * Tasks tab and the dedicated TaskListDetailPage can use it.
 *
 * Edits title / description / status / assignee / due date. Also
 * exposes a Delete task action. Backend gates the actual update
 * + delete; the modal surfaces server errors inline.
 */

import { useEffect, useState, type FormEvent } from 'react';
import {
  ApiClientError,
  groupTasksApi,
  usersSearchApi,
  type GroupTask,
  type GroupTaskStatus,
  type UserSearchResult,
} from '@/services/api';

export const TASK_STATUS_LABEL: Record<GroupTaskStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
};

export const TASK_STATUS_PILL: Record<GroupTaskStatus, string> = {
  open: 'bg-white/10 text-white/70',
  in_progress: 'bg-blue-500/20 text-blue-300',
  blocked: 'bg-red-500/20 text-red-300',
  done: 'bg-green-500/20 text-green-300',
};

export function TaskEditModal({
  task,
  onClose,
  onSaved,
}: {
  task: GroupTask;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [status, setStatus] = useState<GroupTaskStatus>(task.status);
  const [assigneeId, setAssigneeId] = useState<string | null>(task.assigneeUserId);
  const [assigneeName, setAssigneeName] = useState<string>(task.assigneeName ?? '');
  const [dueLocal, setDueLocal] = useState<string>(() => {
    if (!task.dueDate) return '';
    const d = new Date(task.dueDate);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  useEffect(() => {
    let cancelled = false;
    const term = search.trim();
    if (term.length < 2) { setResults([]); return; }
    usersSearchApi.search(term, 10).then((rows) => { if (!cancelled) setResults(rows); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [search]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const dueISO = dueLocal ? new Date(`${dueLocal}T00:00:00.000Z`).toISOString() : null;
      await groupTasksApi.updateTask(task.id, {
        title: title.trim(),
        description: description.trim() || null,
        status,
        assigneeUserId: assigneeId,
        dueDate: dueISO,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    setDeleting(true);
    try {
      await groupTasksApi.deleteTask(task.id);
      onSaved();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <form
        className="card max-w-xl w-full space-y-3"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Edit task</h2>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div>
          <label className="block text-xs text-white/60 mb-1">Title</label>
          <input className="input" value={title} maxLength={500} onChange={(e) => setTitle(e.target.value)} required />
        </div>

        <div>
          <label className="block text-xs text-white/60 mb-1">Description</label>
          <textarea
            className="input"
            rows={4}
            value={description}
            maxLength={8000}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional details…"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-white/60 mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as GroupTaskStatus)} className="input py-1.5">
              {Object.entries(TASK_STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Due date</label>
            <input
              type="date"
              className="input py-1.5"
              value={dueLocal}
              onChange={(e) => setDueLocal(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-white/60 mb-1">Assignee</label>
          {assigneeId ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/90">{assigneeName || assigneeId.slice(0, 8) + '…'}</span>
              <button
                type="button"
                onClick={() => { setAssigneeId(null); setAssigneeName(''); }}
                className="text-xs text-red-300 hover:text-red-200"
              >
                Unassign
              </button>
            </div>
          ) : (
            <div>
              <input
                type="text"
                className="input py-1.5"
                placeholder="Search users…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {results.length > 0 && (
                <ul className="mt-1 max-h-40 overflow-auto rounded border border-white/10 bg-black/30">
                  {results.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => { setAssigneeId(u.id); setAssigneeName(u.displayName); setSearch(''); setResults([]); }}
                        className="w-full text-left px-3 py-1.5 hover:bg-white/5 text-sm"
                      >
                        <span className="text-white font-medium">{u.displayName}</span>
                        <span className="text-white/40 ml-2 text-xs">{u.email ?? '—'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <div className="flex justify-between items-center pt-2">
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="text-xs text-red-400 hover:text-red-300"
          >
            {deleting ? 'Deleting…' : 'Delete task'}
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button type="submit" disabled={saving || !title.trim()} className="btn-primary text-sm">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default TaskEditModal;
